/**
 * Effective state of a deck analysis, resolved at READ time.
 *
 * WHY THIS IS NOT JUST A COLUMN
 * -----------------------------
 * The worker that advances an analysis can be killed outright. Supabase edge
 * isolates are terminated when they exceed the 256 MB memory ceiling or the
 * wall-clock budget, and termination does not run a catch block, a `finally`,
 * or an `unhandledrejection` handler. Nothing gets written.
 *
 * So a row reading `analysing` is genuinely ambiguous: the work may be in
 * flight, or the process that owned it may have died twenty minutes ago. On
 * 2026-08-01 an 18.5 MB deck (Octotronic) hit exactly this — the isolate died
 * decoding a 24.7 MB base64 body, and the analysis left no error, no log line
 * and no row update. Storing a status alone would have reproduced that silence
 * behind a spinner that never stops.
 *
 * Elapsed time is the only signal that separates the two, so the resolution
 * happens on read and the stored row stays a faithful record of what was
 * actually written.
 */

/**
 * How long a non-terminal analysis may go without an update before it is
 * presumed dead.
 *
 * Floor is the paid-plan wall clock (400s) — a background task cannot legally
 * outlive it, so anything past that is not "still working". The margin above
 * covers queueing and clock skew between the worker and the reader. Too tight
 * and a merely-slow deck is declared dead while it is still running, which is
 * the original bug from the opposite direction.
 */
export const STALE_AFTER_MS = 600_000; // 10 minutes

export type AnalysisStatus = 'analysing' | 'scoring' | 'done' | 'failed';

/** Statuses from which no further transition is expected. */
const TERMINAL: ReadonlySet<AnalysisStatus> = new Set<AnalysisStatus>(['done', 'failed']);

export interface AnalysisRow {
  status: AnalysisStatus;
  /** Epoch ms of the last write to this row. */
  updated_at: number;
  failure_reason: string | null;
}

export interface ResolvedAnalysisState {
  status: AnalysisStatus;
  /**
   * True when the stored status is non-terminal but the row has gone quiet for
   * longer than a run can legally take. Callers surface this as a failure the
   * investor can retry — distinct from a failure the worker diagnosed itself.
   */
  presumedDead: boolean;
  failureReason: string | null;
}

export function resolveAnalysisState(
  row: AnalysisRow,
  now: number,
): ResolvedAnalysisState {
  const isTerminal = TERMINAL.has(row.status);

  // A worker that reached its own catch block knows more than a clock does.
  // Never overwrite a real diagnosis with the generic one.
  if (isTerminal) {
    return { status: row.status, presumedDead: false, failureReason: row.failure_reason };
  }

  if (now - row.updated_at > STALE_AFTER_MS) {
    return { status: 'failed', presumedDead: true, failureReason: 'timed_out' };
  }

  return { status: row.status, presumedDead: false, failureReason: row.failure_reason };
}
